import { type Calendar, calendarQueries } from "@/components/calendars/api.ts";
import { useRegisteredCalendarColors } from "@/components/calendars/calendarColors.ts";
import { connectorQueries } from "@/components/connectors/api.ts";
import {
  parseSelectedCalendarIds,
  stringifySelectedCalendarIds,
} from "@/components/preferences/api.ts";
import { useUpdateSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import {
  type BusyCalendarGroup as SharedBusyCalendarGroup,
  groupBusyCalendars,
} from "@/shared/calendars/busyCalendarGroups.ts";
import { isOkthxCalendarId } from "@/types/calendarEvent.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

/** One connector's calendars, in display order. */
// The grouping itself lives in shared/client so native's availability screen
// buckets and labels identically.
export type BusyCalendarGroup = SharedBusyCalendarGroup<Calendar>;

/**
 * The "which calendars count as you being busy" list, and the one way to change
 * it: the calendars grouped by connector, which of them are picked, and a toggle
 * that saves as it goes.
 *
 * Shared rather than reimplemented per surface. The same question is asked in
 * Settings, on the canvas picker card, and in the omnibox picker card, and the
 * rules it answers are subtle enough to drift the moment there are two copies —
 * okthx calendars are excluded (they hold no busy time), primary calendars are
 * always busy and can't be turned off, and a toggle writes the whole set back
 * including the primaries the UI never let the user touch.
 *
 * Saving is per-toggle rather than behind a Save button, so a user who abandons
 * a card halfway keeps every choice they had already made. Rows stay live while
 * a save is in flight: each toggle applies optimistically and the preferences
 * mutation sends them in order, so a slow save never locks the list.
 *
 * @param selectedCalendarIds The saved preference string, passed in rather than
 *   read here so a caller already holding the preferences (every one of them
 *   does) doesn't subscribe twice.
 */
export function useBusyCalendars(selectedCalendarIds: string | undefined) {
  const queryClient = useQueryClient();
  const updateCalendarsPreferences = useUpdateSurfacePreferences("calendars");

  const { data: allCalendars, isLoading } = useQuery(calendarQueries.list());
  const { data: connectorsData } = useQuery(connectorQueries.list());

  // Feed the colour registry from the list this hook already owns, so a surface
  // that mounts without the calendar view (a picker card in a thread) still
  // paints real colours. Registration is a render-time useMemo upstream, so the
  // first render that sees a refetched list already reads its new colours.
  useRegisteredCalendarColors(allCalendars);

  // An okthx calendar holds no busy time, so offering it here would imply an
  // influence it does not have — and picking it would write it back into the
  // availability preference the backend just stopped putting in.
  const calendars = useMemo(
    () => allCalendars?.filter((cal) => !isOkthxCalendarId(cal.calendarId)),
    [allCalendars],
  );

  // Primary anywhere: always busy, never toggleable.
  const primaryCalendarIds = useMemo(
    () => new Set((calendars ?? []).filter((c) => c.primary).map((c) => c.calendarId)),
    [calendars],
  );

  const groups = useMemo<BusyCalendarGroup[]>(
    () => (calendars ? groupBusyCalendars(calendars, connectorsData?.connectors ?? []) : []),
    [calendars, connectorsData],
  );

  const selectedIds = useMemo(
    () => new Set(selectedCalendarIds ? parseSelectedCalendarIds(selectedCalendarIds) : []),
    [selectedCalendarIds],
  );

  const isPrimary = useCallback(
    (calendarId: string) => primaryCalendarIds.has(calendarId),
    [primaryCalendarIds],
  );
  const isSelected = useCallback(
    (calendarId: string) => primaryCalendarIds.has(calendarId) || selectedIds.has(calendarId),
    [primaryCalendarIds, selectedIds],
  );

  /**
   * Flip one calendar and save. The preferences mutation updates its cache
   * optimistically, so `selectedCalendarIds` reflects the change on the next
   * render and the row doesn't wait on the round trip.
   */
  const toggle = useCallback(
    async (calendarId: string) => {
      if (primaryCalendarIds.has(calendarId)) return;
      try {
        // Rebuild from the saved non-primary picks and re-add every primary, so
        // a primary that was never rendered as a row still survives the write.
        const savedNonPrimary = selectedCalendarIds
          ? parseSelectedCalendarIds(selectedCalendarIds).filter(
              (id) => !primaryCalendarIds.has(id),
            )
          : [];
        const nextNonPrimary = savedNonPrimary.includes(calendarId)
          ? savedNonPrimary.filter((id) => id !== calendarId)
          : [...savedNonPrimary, calendarId];
        await updateCalendarsPreferences({
          selectedCalendarIds: stringifySelectedCalendarIds([
            ...new Set([...nextNonPrimary, ...primaryCalendarIds]),
          ]),
        });
        await queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      } catch {
        errorToast("Failed to update calendars. Please try again.");
      }
    },
    [primaryCalendarIds, selectedCalendarIds, updateCalendarsPreferences, queryClient],
  );

  return {
    groups,
    isLoading,
    /** Whether the calendars span more than one connected account. */
    hasMultipleConnectors: groups.length > 1,
    isPrimary,
    isSelected,
    toggle,
  };
}
